import { CartLinesUpdateEvent } from '@shopify/events';

const BUTTON_SELECTOR =
  'biscuits-bundle-form button[data-add-to-cart], button[data-gs-biscuits-sticky-button]';
const POLL_INTERVAL_MS = 300;
const MAX_POLL_ATTEMPTS = 30;
const pendingButtons = new WeakSet();

function currentCartCount() {
  const count = document.querySelector('[data-testid="cart-bubble"]')?.textContent;
  return Number.parseInt(count || '0', 10) || 0;
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) throw new Error(`Failed to refresh cart: ${response.status}`);
  return response.json();
}

function settleButton(button, succeeded) {
  button.dataset.biscuitsLoading = 'false';
  button.dataset.biscuitsSuccess = succeeded ? 'true' : 'false';
  button.dispatchEvent(
    new CustomEvent('gs:biscuits-cart-settled', {
      bubbles: true,
      detail: { succeeded },
    })
  );

  if (succeeded) {
    window.setTimeout(() => {
      button.dataset.biscuitsSuccess = 'false';
    }, 2000);
  }
}

async function synchronizeNativeCart(button, countBefore, variantId) {
  try {
    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, POLL_INTERVAL_MS));

      const ajaxCart = await fetchCart();
      if (ajaxCart.item_count <= countBefore) continue;

      const deferred = CartLinesUpdateEvent.createPromise();
      button.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [
            {
              merchandiseId: variantId,
              quantity: ajaxCart.item_count - countBefore,
            },
          ],
          promise: deferred.promise,
        })
      );

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: false,
          itemCount: ajaxCart.item_count,
          items: ajaxCart.items,
          source: 'biscuits-bundle',
          sourceId: button.dataset.biscuitsLinkedBy || '',
        },
      });

      settleButton(button, true);
      return;
    }

    settleButton(button, false);
  } catch (error) {
    settleButton(button, false);
    console.warn('[biscuits-cart-bridge] Native cart synchronization failed:', error);
  } finally {
    pendingButtons.delete(button);
  }
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const clickedButton = target.closest(BUTTON_SELECTOR);
    if (!(clickedButton instanceof HTMLButtonElement) || clickedButton.disabled) return;

    const appForm = clickedButton.closest('biscuits-bundle-form');
    if (appForm?.dataset.gsBiscuitsInventoryIssue === 'true') return;

    const nativeButton =
      clickedButton.matches('[data-gs-biscuits-sticky-button]') &&
      clickedButton
        .closest('[data-gs-biscuits-enhancements]')
        ?.querySelector('biscuits-bundle-form button[data-add-to-cart]');
    const button = nativeButton instanceof HTMLButtonElement ? nativeButton : clickedButton;
    if (pendingButtons.has(button)) return;

    const variantId =
      button
        .closest('biscuits-bundle-form')
        ?.querySelector('input[name="items[0][id]"], input[name="id"]')?.value ||
      button.value ||
      '';

    button.dataset.biscuitsLoading = 'true';
    button.dataset.biscuitsSuccess = 'false';
    pendingButtons.add(button);
    void synchronizeNativeCart(button, currentCartCount(), variantId);
  },
  { capture: true }
);
